import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useDeleteFeedSource, useFeedSources, usePollFeedSource, useUpdateFeedSource } from 'src/api/queries';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Skeleton } from 'src/components/ui/Skeleton';
import { formatDateTime } from 'src/lib/time';
import type { EditorialFeedSourceWithStats, FeedPollStatus } from 'src/types/editorial';

/**
 * Answers: which sites do we read, and is each one still answering?
 *
 * The sibling of Orígenes. A channel is information we listen to — the box captures it and the
 * whole editorial pipeline runs over what was said. A source is information we read, and nothing
 * runs over it: the reader keeps a copy of each post and stops. That distinction is the first
 * thing this screen has to say, because everything else about the two looks alike.
 */
export function FeedSourcesWorkspace() {
  const sources = useFeedSources();

  return (
    <div className="page-medium">
      <PageHeader title="Suscripciones">
        Los sitios cuyo feed leemos cada quince minutos. De cada publicación guardamos una copia —
        título, resumen y liga— y nada más: no se transcribe, no se verifica y no se redacta nada a
        partir de ellas. Una fuente pausada queda registrada pero ya no se consulta.
      </PageHeader>

      {sources.isPending && <Skeleton variant="rows" count={3} />}
      {sources.isError && <LoadFailure title="No se pudo cargar la lista" error={sources.error} onRetry={sources.refetch} />}

      {sources.data && (
        <ul className="feeds">
          {sources.data.sources.length === 0 && <li className="feeds__empty">Ningún sitio suscrito todavía.</li>}
          {sources.data.sources.map(source => (
            <SourceRow key={source.sourceId} source={source} />
          ))}
        </ul>
      )}

      {/* Adding one is its own screen: the address is read and shown before anything is written,
          which is the difference between "con fallas" appearing in this list and knowing why. */}
      <Link className="feeds__add" to="/suscripciones/nueva">
        Suscribir un sitio
      </Link>
    </div>
  );
}

const STATUS_LABEL = {
  pending: 'Sin leer',
  ok: 'Leyendo',
  unchanged: 'Sin cambios',
  error: 'Con fallas',
} satisfies Record<FeedPollStatus, string>;

/** A feed that answers 304 is healthy and quiet; only `error` is a problem worth the alarm tone. */
const STATUS_TONE = {
  pending: 'unresolved',
  ok: 'supported',
  unchanged: 'supported',
  error: 'contradicted',
} satisfies Record<FeedPollStatus, 'supported' | 'unresolved' | 'contradicted'>;

function SourceRow({ source }: { source: EditorialFeedSourceWithStats }) {
  const update = useUpdateFeedSource();
  const poll = usePollFeedSource();
  const remove = useDeleteFeedSource();
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const active = source.active;

  return (
    <li className="feed" data-status={active ? 'active' : 'inactive'}>
      <div className="feed__who">
        <p className="feed__name">{source.name}</p>
        <a className="feed__url mono" href={source.siteUrl || source.feedUrl} target="_blank" rel="noreferrer noopener">
          {source.feedUrl}
        </a>
        {source.lastStatus === 'error' && source.lastError && <p className="feed__error">{source.lastError}</p>}
      </div>

      <div className="feed__stats">
        <Badge tone={active ? STATUS_TONE[source.lastStatus] : 'unresolved'}>{active ? STATUS_LABEL[source.lastStatus] : 'Pausada'}</Badge>
        <span className="feed__stat">
          {source.itemCount} publicación{source.itemCount === 1 ? '' : 'es'}
        </span>
        <span className="feed__since">{source.lastFetchedAt ? `leída ${formatDateTime(source.lastFetchedAt)}` : 'nunca leída'}</span>
        <Link className="feed__open" to={`/publicaciones?fuente=${encodeURIComponent(source.sourceId)}`}>
          Ver publicaciones
        </Link>
      </div>

      <div className="feed__actions">
        <Button variant="ghost" busy={poll.isPending} onClick={() => poll.mutate(source.sourceId)}>
          Leer ahora
        </Button>
        <Button variant="ghost" busy={update.isPending} onClick={() => update.mutate({ sourceId: source.sourceId, active: !active })}>
          {active ? 'Pausar' : 'Activar'}
        </Button>
        {/* Two steps, because unsubscribing is the one action here that cannot be undone with the
            same click. Las copias se quedan — eso es lo que dice la confirmación. */}
        {confirmingRemoval ? (
          <>
            <Button variant="ghost" busy={remove.isPending} onClick={() => remove.mutate(source.sourceId)}>
              Confirmar
            </Button>
            <Button variant="ghost" onClick={() => setConfirmingRemoval(false)}>
              Cancelar
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => setConfirmingRemoval(true)}>
            Quitar
          </Button>
        )}
      </div>

      {confirmingRemoval && <p className="feed__note">Dejaremos de leerla. Las publicaciones ya copiadas se conservan.</p>}
      {poll.isError && <p className="feed__error">{poll.error instanceof Error ? poll.error.message : 'No se pudo leer'}</p>}
      {update.isError && <p className="feed__error">{update.error instanceof Error ? update.error.message : 'No se pudo cambiar'}</p>}
    </li>
  );
}
